import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Pencil, Undo2 } from 'lucide-react';
import { Button, ErrorState, Input, Modal, Select, Skeleton } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatMoney } from '../../lib/format';
import {
  useAgreements,
  useTimesheetReview,
  useUpdateEntry,
  type TimeAgreement,
  type ApprovalRow,
  type ReviewCheckKind,
  type ReviewEntry,
  type TimesheetReview,
} from './api';
import { formatHoursUnit, formatHoursValue, parseHoursInput, type HoursFormat } from './hours';
import { TimesheetNote } from './timesheet-status';

/**
 * REVIEW ONE SUBMITTED PERIOD — its own entries and nothing else.
 *
 * Jesse, 2026-10-05: *"review should only consist of the entries that were
 * submitted for review ... no others and it should be in a view that is easy to
 * read, isolate time entries, and confirm their amounts."*
 *
 * - Every line is hours × cost rate = amount, and the footing is the sum of
 *   the lines — the same figure the queue row printed (one rule, server side).
 * - "Worth a look" and "By project" both ISOLATE: press one and only its
 *   entries are listed; Show all puts the rest back.
 * - A tick per line lets the reviewer mark what they have checked. It is a
 *   reading aid for this sitting, not a record — nothing is saved.
 */

/** Short words for a flag, on the line it is about. */
function flagLabel(kind: ReviewCheckKind, entry: ReviewEntry): string {
  switch (kind) {
    case 'no-note':
      return 'no note';
    case 'long-day':
      return 'over 10 h that day';
    case 'weekend':
      return 'weekend';
    case 'logged-late':
      return entry.loggedOn ? `logged ${daysBetween(entry.date, entry.loggedOn)} days later` : 'logged late';
    case 'edited':
      return entry.editedBy ? `changed by ${entry.editedBy.name}` : 'changed by someone else';
    case 'duplicate':
      return 'possible duplicate';
    case 'timer-cap':
      return 'timer hit its cap';
    case 'no-rate':
      return 'no cost rate';
    case 'deleted':
      return 'deleted in Harvest';
    case 'inactive-agreement':
      return 'archived or ended agreement';
    default:
      return kind;
  }
}

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** `2026-09-02` → `Wed, Sep 2` — read in UTC, so the day never slips. */
function dayHeading(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const projectKey = (e: ReviewEntry) => `${e.client ?? ''}|${e.agreement}`;
const projectName = (e: ReviewEntry) => (e.client ? `${e.client} — ${e.agreement}` : e.agreement);

type Focus = { kind: 'check'; check: ReviewCheckKind; text: string } | { kind: 'project'; key: string; name: string } | null;

export function ReviewDialog({
  row,
  hoursFormat,
  canEdit,
  approving,
  onClose,
  onApprove,
  onSendBack,
}: {
  row: ApprovalRow | null;
  hoursFormat: HoursFormat;
  /**
   * An org admin may correct a line in place — project, task, hours, note.
   * The server allows it in a SUBMITTED period for an admin only, and logs it
   * as a change by someone else, which the review then shows.
   */
  canEdit: boolean;
  approving: boolean;
  onClose: () => void;
  onApprove: (row: ApprovalRow) => void;
  onSendBack: (row: ApprovalRow) => void;
}) {
  const review = useTimesheetReview(row?.id ?? null);
  const [focus, setFocus] = useState<Focus>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  useEffect(() => {
    setFocus(null);
    setChecked(new Set());
  }, [row?.id]);

  if (!row) return null;
  const data = review.data;
  const canDecide = row.can.approve;

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title={`Review · ${row.person.name}`}
      description={data ? `${data.timesheet.label} · only the entries in this period` : row.label}
      className="max-w-5xl"
      footer={
        canDecide ? (
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            {data && data.entries.length > 0 && (
              <span className="mr-auto text-caption text-grey-500">
                <span className="num">{checked.size}</span> of <span className="num">{data.entries.length}</span>{' '}
                checked
              </span>
            )}
            <Button variant="ghost" onClick={() => onSendBack(row)}>
              <Undo2 className="h-4 w-4" aria-hidden />
              Send back
            </Button>
            <Button loading={approving} onClick={() => onApprove(row)}>
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              Approve
            </Button>
          </div>
        ) : undefined
      }
    >
      {review.isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : review.isError || !data ? (
        <ErrorState
          title="Couldn't load this timesheet"
          message={review.error instanceof Error ? review.error.message : undefined}
          onRetry={() => void review.refetch()}
        />
      ) : (
        <ReviewBody
          data={data}
          hoursFormat={hoursFormat}
          canEdit={canEdit}
          focus={focus}
          setFocus={setFocus}
          checked={checked}
          toggle={(id) =>
            setChecked((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            })
          }
        />
      )}
    </Modal>
  );
}

function ReviewBody({
  data,
  hoursFormat,
  canEdit,
  focus,
  setFocus,
  checked,
  toggle,
}: {
  data: TimesheetReview;
  hoursFormat: HoursFormat;
  canEdit: boolean;
  focus: Focus;
  setFocus: (f: Focus) => void;
  checked: Set<string>;
  toggle: (id: string) => void;
}) {
  const { totals, entries, checks, usual, timesheet } = data;
  const salaried = totals.costBasis === 'salary';
  const agreements = useAgreements();
  /** The one line open for correction, if any. */
  const [editingId, setEditingId] = useState<string | null>(null);

  const projects = useMemo(() => {
    const map = new Map<string, { key: string; name: string; hours: number; amount: number; count: number }>();
    for (const e of entries) {
      const key = projectKey(e);
      const p = map.get(key) ?? { key, name: projectName(e), hours: 0, amount: 0, count: 0 };
      p.hours += e.hours;
      p.amount += e.amount ?? 0;
      p.count += 1;
      map.set(key, p);
    }
    return [...map.values()].sort((a, b) => b.hours - a.hours);
  }, [entries]);

  const visible = useMemo(() => {
    if (!focus) return entries;
    if (focus.kind === 'project') return entries.filter((e) => projectKey(e) === focus.key);
    const ids = new Set(checks.find((c) => c.kind === focus.check)?.entryIds ?? []);
    return entries.filter((e) => ids.has(e.id));
  }, [entries, checks, focus]);

  const days = useMemo(() => {
    const map = new Map<string, ReviewEntry[]>();
    for (const e of visible) map.set(e.date, [...(map.get(e.date) ?? []), e]);
    return [...map.entries()];
  }, [visible]);

  const money = (n: number) => formatMoney(n);
  const hoursOf = (list: ReviewEntry[]) => list.reduce((s, e) => s + e.hours, 0);
  const amountOf = (list: ReviewEntry[]) => Math.round(list.reduce((s, e) => s + (e.amount ?? 0), 0) * 100) / 100;
  const entryChecks = checks.filter((c) => c.entryIds.length > 0);
  const periodChecks = checks.filter((c) => c.entryIds.length === 0);

  return (
    <div className="max-h-[68vh] space-y-5 overflow-y-auto pr-1">
      {/* The period in one line — the figures the reviewer is approving. */}
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="num text-heading-3 font-bold text-secondary">{formatHoursUnit(totals.hours, hoursFormat)}</span>
        <span className="text-body text-secondary">
          {salaried ? (
            'salaried'
          ) : totals.costBasis === 'no-rate' ? (
            <span className="text-amber-700">no cost rate</span>
          ) : (
            <>
              <span className="num font-bold">{money(totals.cost ?? 0)}</span> cost
            </>
          )}
        </span>
        <span className="text-body-sm text-grey-500">
          <span className="num">{totals.entries}</span> {totals.entries === 1 ? 'entry' : 'entries'} ·{' '}
          <span className="num">{totals.days}</span> {totals.days === 1 ? 'day' : 'days'}
          {usual && (
            <>
              {' '}
              · usual <span className="num">{formatHoursUnit(usual.hours, hoursFormat)}</span> a {usual.periodNoun}
            </>
          )}
        </span>
      </div>
      {timesheet.note && <TimesheetNote note={timesheet.note} author={timesheet.person.name} />}

      {/* What is worth a look — each one isolates its entries. */}
      <section>
        <h3 className="label !mb-1.5">Worth a look</h3>
        {checks.length === 0 ? (
          <p className="flex items-center gap-1.5 text-body-sm text-grey-500">
            <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
            Nothing stands out.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {entryChecks.map((c) => {
              const on = focus?.kind === 'check' && focus.check === c.kind;
              return (
                <li key={c.kind}>
                  <button
                    type="button"
                    onClick={() => setFocus(on ? null : { kind: 'check', check: c.kind, text: c.text })}
                    aria-pressed={on}
                    className={cn(
                      'rounded-lg border px-2.5 py-1 text-left text-body-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
                      on
                        ? 'border-amber-400 bg-amber-50 text-secondary'
                        : 'border-grey-200 bg-white text-grey-700 hover:border-amber-300 hover:bg-amber-50/60',
                    )}
                  >
                    {c.text}
                  </button>
                </li>
              );
            })}
            {periodChecks.map((c) => (
              <li key={c.kind} className="rounded-lg border border-grey-200 bg-grey-50 px-2.5 py-1 text-body-sm text-grey-700">
                {c.text}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* By project — the amount per agreement, and a way to isolate one. */}
      {projects.length > 1 && (
        <section>
          <h3 className="label !mb-1.5">By project</h3>
          <ul className="divide-y divide-grey-100 rounded-lg border border-grey-200">
            {projects.map((p) => {
              const on = focus?.kind === 'project' && focus.key === p.key;
              return (
                <li key={p.key}>
                  <button
                    type="button"
                    onClick={() => setFocus(on ? null : { kind: 'project', key: p.key, name: p.name })}
                    aria-pressed={on}
                    className={cn(
                      'grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-x-4 px-3 py-1.5 text-left text-body-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
                      on ? 'bg-accent/5' : 'hover:bg-grey-50',
                    )}
                  >
                    <span className="min-w-0 break-words text-secondary">{p.name}</span>
                    <span className="num text-right text-grey-600">{formatHoursValue(p.hours, hoursFormat)}</span>
                    <span className="num w-24 text-right font-semibold text-secondary">
                      {salaried ? '' : money(Math.round(p.amount * 100) / 100)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* The entries, day by day. */}
      <section>
        <div className="mb-1.5 flex flex-wrap items-center gap-x-2">
          <h3 className="label !mb-0">Entries</h3>
          {focus && (
            <span className="text-caption text-grey-500">
              showing <span className="num">{visible.length}</span> of <span className="num">{entries.length}</span> ·{' '}
              {focus.kind === 'project' ? focus.name : focus.text}
              <button
                type="button"
                onClick={() => setFocus(null)}
                className="ml-2 font-semibold text-accent-700 hover:underline"
              >
                Show all
              </button>
            </span>
          )}
        </div>
        {entries.length === 0 ? (
          <p className="text-body-sm text-grey-500">Nothing was logged in this period.</p>
        ) : (
          <div className="rounded-lg border border-grey-200">
            {/* Column heads — desktop only; a phone reads each line as a card. */}
            <div className="hidden grid-cols-[1.25rem_minmax(0,1.1fr)_minmax(0,1.6fr)_4.5rem_5.5rem_6rem] gap-x-3 border-b border-grey-200 bg-grey-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-grey-500 md:grid">
              <span />
              <span>Project</span>
              <span>Note</span>
              <span className="text-right">Hours</span>
              <span className="text-right">Rate</span>
              <span className="text-right">Amount</span>
            </div>
            {days.map(([day, list]) => (
              <div key={day} className="border-b border-grey-100 last:border-b-0">
                <div className="flex items-baseline justify-between gap-3 bg-grey-50/60 px-3 py-1 text-caption">
                  <span className="font-semibold text-grey-700">{dayHeading(day)}</span>
                  <span className="text-grey-600">
                    <span className="num">{formatHoursUnit(hoursOf(list), hoursFormat)}</span>
                    {!salaried && (
                      <>
                        {' · '}
                        <span className="num">{money(amountOf(list))}</span>
                      </>
                    )}
                  </span>
                </div>
                <ul>
                  {list.map((e) =>
                    editingId === e.id ? (
                      <EntryEditor
                        key={e.id}
                        entry={e}
                        agreements={agreements.data?.agreements ?? []}
                        hoursFormat={hoursFormat}
                        onClose={() => setEditingId(null)}
                      />
                    ) : (
                      <EntryLine
                        key={e.id}
                        entry={e}
                        hoursFormat={hoursFormat}
                        salaried={salaried}
                        checked={checked.has(e.id)}
                        onToggle={() => toggle(e.id)}
                        onEdit={canEdit && editingId === null ? () => setEditingId(e.id) : undefined}
                      />
                    ),
                  )}
                </ul>
              </div>
            ))}
            {/* The footing — the lines shown, added up. */}
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-x-4 border-t border-grey-200 bg-grey-50 px-3 py-2 text-body-sm font-semibold text-secondary">
              <span>
                {focus ? 'Shown' : 'Total'} · <span className="num">{visible.length}</span>{' '}
                {visible.length === 1 ? 'entry' : 'entries'}
              </span>
              <span className="num text-right">{formatHoursUnit(hoursOf(visible), hoursFormat)}</span>
              <span className="num w-24 text-right">{salaried ? '' : money(amountOf(visible))}</span>
            </div>
          </div>
        )}
        {!salaried && totals.uncostedHours > 0 && (
          <p className="mt-1.5 text-caption text-amber-700">
            <span className="num">{formatHoursValue(totals.uncostedHours, hoursFormat)}</span> h have no cost rate and are
            not in the amount.
          </p>
        )}
      </section>
    </div>
  );
}

function EntryLine({
  entry: e,
  hoursFormat,
  salaried,
  checked,
  onToggle,
  onEdit,
}: {
  entry: ReviewEntry;
  hoursFormat: HoursFormat;
  salaried: boolean;
  checked: boolean;
  onToggle: () => void;
  /** Present when this viewer may correct the line (an admin). */
  onEdit?: () => void;
}) {
  // What stops a correction, said on the line rather than on a dead button.
  const lockedBy = e.invoiced
    ? 'on an invoice — void it to change'
    : e.deletedInSource
      ? 'deleted in Harvest'
      : null;
  return (
    <li
      className={cn(
        'grid grid-cols-[1.25rem_minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 px-3 py-2 md:grid-cols-[1.25rem_minmax(0,1.1fr)_minmax(0,1.6fr)_4.5rem_5.5rem_6rem] md:items-baseline',
        checked && 'bg-success/[0.05]',
        e.deletedInSource && 'opacity-60',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        aria-label={`Checked: ${e.agreement}, ${e.hours} hours`}
        className="mt-0.5 h-4 w-4 cursor-pointer rounded border-grey-300 text-success focus:ring-success/30"
      />
      <div className="min-w-0">
        <p className="truncate text-body-sm font-semibold text-secondary">{e.client ?? e.agreement}</p>
        <p className="truncate text-caption text-grey-500">
          {e.client ? e.agreement : ''}
          {e.task ? `${e.client ? ' · ' : ''}${e.task}` : ''}
          {!e.billable && <span className="ml-1 text-grey-400">· non-billable</span>}
        </p>
      </div>
      {/* Phone: the amount sits on the project line. */}
      <span className="num text-right text-body-sm font-semibold text-secondary md:hidden">
        {salaried ? formatHoursUnit(e.hours, hoursFormat) : e.amount === null ? '—' : formatMoney(e.amount)}
      </span>
      <div className="col-span-2 col-start-2 min-w-0 md:col-span-1 md:col-start-auto">
        <p className={cn('text-body-sm', e.notes?.trim() ? 'text-grey-700' : 'italic text-grey-400')}>
          {e.notes?.trim() || 'No note'}
        </p>
        {e.editedBy && (
          <p className="text-caption text-grey-500">
            Changed by {e.editedBy.name}: {e.editedBy.summary}
          </p>
        )}
        {e.flags.length > 0 && (
          <p className="mt-0.5 flex flex-wrap gap-1">
            {e.flags.map((f) => (
              <span
                key={f}
                className="rounded bg-amber-50 px-1.5 py-px text-[11px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-200"
              >
                {flagLabel(f, e)}
              </span>
            ))}
          </p>
        )}
        {onEdit &&
          (lockedBy ? (
            <p className="mt-0.5 text-caption text-grey-400">Can't edit — {lockedBy}</p>
          ) : (
            <button
              type="button"
              onClick={onEdit}
              className="mt-0.5 inline-flex items-center gap-1 text-caption font-semibold text-accent-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            >
              <Pencil className="h-3 w-3" aria-hidden />
              Edit
            </button>
          ))}
        {/* Phone: the arithmetic, under the note. */}
        {!salaried && (
          <p className="num text-caption text-grey-500 md:hidden">
            {formatHoursUnit(e.hours, hoursFormat)}
            {e.rate !== null && <> × {formatMoney(e.rate)}/h</>}
          </p>
        )}
      </div>
      <span className="num hidden text-right text-body-sm text-secondary md:block">
        {formatHoursValue(e.hours, hoursFormat)}
      </span>
      <span className="num hidden text-right text-body-sm text-grey-500 md:block">
        {salaried ? '' : e.rate === null ? '—' : `${formatMoney(e.rate)}/h`}
      </span>
      <span className="num hidden text-right text-body-sm font-semibold text-secondary md:block">
        {salaried ? '' : e.amount === null ? '—' : formatMoney(e.amount)}
      </span>
    </li>
  );
}

/**
 * Correct one line in place: project, task, hours, note. Only what changed is
 * sent, through `PATCH /time/:id` — the one writer every door uses. A project
 * move re-prices the hour at the new agreement's rate (the server's rule), and
 * the review refetches, so the line's amount and the footing follow.
 */
function EntryEditor({
  entry: e,
  agreements,
  hoursFormat,
  onClose,
}: {
  entry: ReviewEntry;
  agreements: TimeAgreement[];
  hoursFormat: HoursFormat;
  onClose: () => void;
}) {
  const update = useUpdateEntry();
  const [agreementId, setAgreementId] = useState(e.agreementId);
  const [taskId, setTaskId] = useState(e.taskId ?? '');
  const [hours, setHours] = useState(formatHoursValue(e.hours, hoursFormat));
  const [notes, setNotes] = useState(e.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  // Active projects, plus this line's own when it is archived — so opening
  // and saving can never quietly move it.
  const projectOptions = useMemo(() => {
    const opts = agreements
      .map((a) => ({ value: a.id, label: `${a.client.name} — ${a.name}` }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
    if (!opts.some((o) => o.value === e.agreementId)) {
      opts.unshift({ value: e.agreementId, label: `${projectName(e)} (archived)` });
    }
    return opts;
  }, [agreements, e]);

  const agreement = agreements.find((a) => a.id === agreementId);
  const taskOptions = useMemo(() => {
    const tasks = agreement?.tasks ?? [];
    const opts = [{ value: '', label: 'No task' }, ...tasks.map((t) => ({ value: t.id, label: t.name }))];
    // The line's current task, kept selectable while the project is unchanged.
    if (agreementId === e.agreementId && e.taskId && !tasks.some((t) => t.id === e.taskId)) {
      opts.push({ value: e.taskId, label: `${e.task ?? 'Current task'} (no longer offered)` });
    }
    return opts;
  }, [agreement, agreementId, e]);

  const pickProject = (id: string) => {
    setAgreementId(id);
    const next = agreements.find((a) => a.id === id);
    // A task the new project does not offer would be refused — keep it only
    // when it is offered there too.
    if (!next?.tasks?.some((t) => t.id === taskId)) setTaskId('');
  };

  const save = () => {
    const parsed = parseHoursInput(hours);
    if (parsed === null) return setError('Hours like 1.5 or 1:30.');
    if (parsed <= 0) return setError('Hours must be more than 0.');
    const patch: {
      id: string;
      agreementId?: string;
      taskId?: string | null;
      hours?: number;
      notes?: string | null;
    } = { id: e.id };
    if (agreementId !== e.agreementId) patch.agreementId = agreementId;
    if ((taskId || null) !== (e.taskId ?? null) || patch.agreementId) patch.taskId = taskId || null;
    if (Math.round(parsed * 100) !== Math.round(e.hours * 100)) patch.hours = parsed;
    const note = notes.trim();
    if (note !== (e.notes ?? '').trim()) patch.notes = note || null;
    if (Object.keys(patch).length === 1) return onClose();
    setError(null);
    void update
      .mutateAsync(patch)
      .then(onClose)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'That change was refused.'));
  };

  return (
    <li className="space-y-2 bg-accent/[0.04] px-3 py-3 ring-1 ring-inset ring-accent/20">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_6rem]">
        <Select
          label="Project"
          selectSize="sm"
          value={agreementId}
          onChange={(ev) => pickProject(ev.target.value)}
          options={projectOptions}
        />
        <Select
          label="Task"
          selectSize="sm"
          value={taskId}
          onChange={(ev) => setTaskId(ev.target.value)}
          options={taskOptions}
        />
        <Input
          label="Hours"
          size="sm"
          inputMode="decimal"
          value={hours}
          onChange={(ev) => setHours(ev.target.value)}
          className="num [&_input]:text-right"
        />
      </div>
      <Input
        label="Note"
        size="sm"
        value={notes}
        onChange={(ev) => setNotes(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') save();
        }}
      />
      {agreementId !== e.agreementId && (
        <p className="text-caption text-grey-500">
          Moving it re-prices the hours at the new project's cost rate.
        </p>
      )}
      {error && <p className="text-body-sm font-medium text-error">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={update.isPending}>
          Cancel
        </Button>
        <Button size="sm" loading={update.isPending} onClick={save}>
          Save
        </Button>
      </div>
    </li>
  );
}
